import { createInventoryItem, resolveItemTypeToMeta } from "game-common/item/item"
import { Inventory, TileSize } from "game-common/models"
import { forestGrassTile } from "game-common/room/ember/grass_art"
import { BLEND_MODES, Container, Graphics, Sprite, Texture } from "pixi.js"
import { contentSheetResource } from "../content_textures"
import { BuildPlacementFeedback } from "../effects/build_placement_feedback"
import { GuiManager } from "../gui_manager"
import { SCALER } from "../../../../client_util"
import { BuildingPrintCountdown } from "./building_print_countdown"
import { glowDisc, shineBand, sparkBurst, REWARD_GOLD, REWARD_GOLD_HOT } from "./chapter_reward_art"
import { easeInCubic, easeInOutCubic, easeOutCubic, lerp, quadPoint, span } from "./chapter_reward_motion"
import { EquippedWidget, SLOT, SLOT_PITCH, TRAY_HEIGHT, TRAY_PAD } from "./equipped"
import { DemoCursor, DemoStamp, drawDioramaFrame, drawRipples, Pt, TutorialDemo } from "./tutorial_demo"

export { raisedStampPose } from "./tutorial_demo"

// The building helper: a little diorama of the camp in which a pointer readies a wall, drags a row
// and lets go, and the row goes up. It is a lesson the player watches rather than a form they read,
// so it is drawn in the camp HUD's own materials (build_board's timber and bronze, the clues' gold
// spark, the chapter checklist's diamonds and stamp) rather than as a panel of numbered sentences.
//
// Everything inside the frame is the REAL thing: the ready bar is the HUD's EquippedWidget, the
// tiles come from the content atlases, and the drag, the counting halo and the build are drawn by
// the game's own BuildPlacementFeedback and BuildingPrintCountdown -- so what the demo shows is
// exactly what the player will see when they try it. Only the chrome around it (step track,
// pointer, the closing flourish) belongs to the demo; the window itself is tutorial_demo.ts.

// The board, and the diorama inside it. CELL must equal TileSize: the placement feedback draws in
// world tiles, and the scene is laid out on the same grid at 1:1.
const CELL = 32
const COLS = 9, ROWS = 5
const SCENE_W = COLS * CELL, SCENE_H = ROWS * CELL
const WIDTH = SCENE_W + 44
const SCENE_X = (WIDTH - SCENE_W) / 2
const SCENE_Y = 92
const CAPTION_Y = SCENE_Y + SCENE_H + 13
const CAPTION_H = 36
const HEIGHT = CAPTION_Y + CAPTION_H + 10
// The wall goes up on the FACE row, three sections from WALL_COL, each top one row above its face.
const WALL_COL = 3, FACE_ROW = 2, WALL_SECTIONS = 3
const WALL_X = SCENE_X + WALL_COL * CELL, WALL_Y = SCENE_Y + (FACE_ROW - 1) * CELL
const WALL_W = WALL_SECTIONS * CELL, WALL_H = 2 * CELL

// ---- The timeline ------------------------------------------------------------------------------
//
// One play-through, posed from its own elapsed milliseconds and then held on the last frame until
// Replay. Everything that moves inside it is a pure function of that clock, so any instant can be
// posed exactly; only effects that run on after an event (sparks, the settle) use the monotonic
// frame clock, which keeps running while the play-through holds.

export const BUILDING_DEMO_MS = 8000
const SELECT_AT = 1000 // the pointer clicks the wall on the ready bar
const DRAG_FROM = 1900 // ...and heads for the ground
const PRESS_AT = 2550
const DRAG_START = 2650, DRAG_END = 3750
const RELEASE_AT = 3950
const PRINT_MS = 2700
const BUILT_AT = RELEASE_AT + PRINT_MS
const STAMP_AT = BUILT_AT + 250
const DONE_AT = BUILT_AT + 450
const REPLAY_AT = 7400
const RIPPLES = [SELECT_AT, PRESS_AT, RELEASE_AT]

const CAPTIONS = [
    "Pick the wall from your ready bar.",
    "Drag across the ground to highlight a row.",
    "Let go and wait. The whole row builds at once.",
]
const DONE_CAPTION = "Your wall is up! Now raise your own."

/** Where the demonstration is at `elapsedMs` into one play-through. */
export const buildingTutorialPose = (elapsedMs: number) => {
    const t = Math.min(BUILDING_DEMO_MS, Math.max(0, elapsedMs))
    const drag = easeInOutCubic(span(t, DRAG_START, DRAG_END))
    return {
        step: t < DRAG_FROM ? 0 : t < RELEASE_AT ? 1 : 2,
        selected: t < SELECT_AT ? 0 : 1,
        drag,
        // A cell lights when the POINTER crosses into it (halfway between two cell centres), not on
        // a timer, so the highlight is seen to follow the hand.
        tiles: t < PRESS_AT ? 0 : 1 + (drag >= 0.25 ? 1 : 0) + (drag >= 0.75 ? 1 : 0),
        released: t >= RELEASE_AT,
        built: t >= BUILT_AT,
        done: t >= DONE_AT,
    }
}

export interface CursorStops { rest: Pt; slot: Pt; start: Pt; end: Pt }

// A hand moves in arcs, not along a ruler.
const glide = (from: Pt, to: Pt, k: number) =>
    quadPoint(from, { x: (from.x + to.x) / 2, y: Math.min(from.y, to.y) - 18 }, to, easeInOutCubic(k))

/** The pointer: where it is, whether it is held down, how visible it is. */
export const buildingCursorPose = (elapsedMs: number, at: CursorStops): DemoCursor => {
    const t = Math.min(BUILDING_DEMO_MS, Math.max(0, elapsedMs))
    let p: Pt
    if (t < DRAG_FROM) p = glide(at.rest, at.slot, span(t, 150, 900))
    else if (t < PRESS_AT) p = glide(at.slot, at.start, span(t, DRAG_FROM, PRESS_AT - 80))
    else if (t < RELEASE_AT) p = { x: lerp(at.start.x, at.end.x, buildingTutorialPose(t).drag), y: at.start.y }
    else {
        // Let go, then step back out of the way of the dial and the build.
        const k = easeInOutCubic(span(t, RELEASE_AT + 120, RELEASE_AT + 700))
        p = { x: at.end.x + 24 * k, y: at.end.y + 30 * k }
    }
    return {
        ...p,
        pressed: (t >= SELECT_AT && t < SELECT_AT + 170) || (t >= PRESS_AT && t < RELEASE_AT),
        alpha: Math.min(easeOutCubic(span(t, 0, 250)), 1 - easeInCubic(span(t, RELEASE_AT + 250, RELEASE_AT + 750))),
    }
}

/** Real game controls and atlas tiles in an isolated scene. All control callbacks are local. */
export class BuildingTutorial extends TutorialDemo {
    private scene = new Container()
    private frame = new Graphics()
    private fx = new Graphics()
    private stamp = new DemoStamp("WALL RAISED")
    private readyBar: EquippedWidget
    private countdown: BuildingPrintCountdown
    private feedback: BuildPlacementFeedback
    private inventory: Inventory = { items: ["axe", "wall-wood"].map((type: any, i) => ({
        ...createInventoryItem(resolveItemTypeToMeta(type), { id: `tutorial-${type}`, quantity: i === 1 ? 3 : 1 }),
        readied: true, active: i === 0,
    })), abilities: {}, lastUpdateTs: 0 }
    private art: { sprite: Sprite; sheet: string; tile: number; parent?: number }[] = []
    // What the play-through has already told the placement feedback, so each event fires once.
    private played = { tiles: 0, released: false, built: false }
    private selected = -1

    constructor(hover: (over: boolean) => void, dismiss: () => void) {
        super({
            width: WIDTH, height: HEIGHT, padX: SCENE_X, contentWidth: SCENE_W,
            captionY: CAPTION_Y, captionH: CAPTION_H, overline: "BUILDING", title: "Raise a wall",
            steps: {
                labels: ["PICK", "DRAG", "BUILD"],
                litAt: [0, DRAG_FROM, RELEASE_AT + 420],
                doneAt: [SELECT_AT, RELEASE_AT, BUILT_AT],
                linkFill: [[SELECT_AT + 120, DRAG_FROM], [RELEASE_AT + 60, RELEASE_AT + 420]],
            },
            durationMs: BUILDING_DEMO_MS, replayAt: REPLAY_AT,
        }, hover, dismiss)

        // The diorama. Sortable, so the placement feedback and the print dial keep their world
        // stacking (dial over feedback over wall ghosts) as they do on the real map.
        this.scene.position.set(SCENE_X, SCENE_Y)
        this.scene.sortableChildren = true
        this.content.addChild(this.scene)
        for (let y = 0; y < ROWS; y++) for (let x = 0; x < COLS; x++) {
            this.addTile("forest-grass", forestGrassTile(x, y), x, y)
        }
        // Three wall sections on one row, each with its matching top directly above its face.
        ;[[257, 273], [257, 273], [258, 274]].forEach(([top, face], x) => {
            this.addTile("fence_medieval", top, WALL_COL + x, FACE_ROW - 1, x)
            this.addTile("fence_medieval", face, WALL_COL + x, FACE_ROW, x)
        })
        this.feedback = new BuildPlacementFeedback(() => this.scene, () => this.clock)
        this.countdown = new BuildingPrintCountdown(() => this.scene)
        this.countdown.scale.set(2, 2 * SCALER)
        // The dial's root sits half a tile into its group's anchor; put it on the middle section.
        const dialX = (WALL_COL + 1.5) * CELL, dialY = (FACE_ROW + 0.5) * CELL
        this.countdown.position.set(dialX - TileSize, dialY - TileSize * SCALER)
        this.scene.addChild(this.countdown)
        drawDioramaFrame(this.frame, SCENE_X, SCENE_Y, SCENE_W, SCENE_H)
        this.content.addChild(this.frame)

        // The ready bar where the HUD keeps it: docked in the bottom-left corner of the view.
        this.readyBar = new EquippedWidget({} as GuiManager)
        this.readyBar.position.set(SCENE_X + 6, SCENE_Y + SCENE_H - TRAY_HEIGHT - 6)
        this.content.addChild(this.readyBar)

        this.fx.blendMode = BLEND_MODES.ADD
        this.content.addChild(this.fx)
        // Centred over the wall, in the free row above its tops.
        this.stamp.place(WALL_X + WALL_W / 2, SCENE_Y + CELL / 2)
        this.content.addChild(this.stamp.burst, this.stamp.plaque)
        this.animate(0)
    }

    private addTile = (sheet: string, tile: number, x: number, y: number, parent?: number) => {
        const sprite = new Sprite(Texture.EMPTY)
        sprite.position.set(x * CELL, y * CELL)
        sprite.width = CELL; sprite.height = CELL
        this.scene.addChild(sprite)
        this.art.push({ sprite, sheet, tile, parent })
    }

    protected rewind() {
        this.feedback.clear()
        this.played = { tiles: 0, released: false, built: false }
    }

    protected poseScene(t: number, deltaMs: number) {
        const pose = buildingTutorialPose(t)

        if (this.selected !== pose.selected) {
            this.selected = pose.selected
            this.inventory.items.forEach((item, i) => item.active = i === pose.selected)
            this.readyBar.update(this.inventory, () => {})
        }
        this.readyBar.animate(deltaMs)

        this.art.forEach(({ sprite, sheet, tile, parent }) => {
            const texture = contentSheetResource(sheet)?.textures?.[`${sheet}-${tile}`] || Texture.EMPTY
            if (sprite.texture !== texture) { sprite.texture = texture; sprite.width = CELL; sprite.height = CELL }
            if (parent === undefined) return
            // The real print queue shows ghost walls until its countdown commits the batch.
            sprite.visible = parent < pose.tiles
            sprite.alpha = pose.built ? 1 : 0.4
        })
        this.playFeedback(pose)

        if (pose.released && !pose.built) {
            this.countdown.setGroups([{ id: 1, anchor: { x: 0, y: 0 }, startedAt: RELEASE_AT, duration: PRINT_MS,
                count: WALL_SECTIONS, onCancel: () => {} }], t)
        } else this.countdown.setGroups([], t)

        this.drawFx(t)
        this.stamp.draw(t - STAMP_AT)
    }

    protected cursorAt(t: number) {
        return buildingCursorPose(t, this.cursorStops())
    }

    protected captionAt(t: number) {
        const pose = buildingTutorialPose(t)
        return { text: pose.done ? DONE_CAPTION : CAPTIONS[pose.step], done: pose.done }
    }

    /** Tell the game's own placement feedback what a real drag would have, once per event. */
    private playFeedback = (pose: ReturnType<typeof buildingTutorialPose>) => {
        const key = (i: number) => `${WALL_COL + i},${FACE_ROW}`
        const cell = (i: number) => ({ x: WALL_COL + i, y: FACE_ROW })
        while (this.played.tiles < pose.tiles && !pose.released) {
            this.feedback.add(key(this.played.tiles), cell(this.played.tiles))
            this.played.tiles++
        }
        if (pose.tiles > 0 && !pose.released) this.feedback.setLineEnd(cell(pose.tiles - 1), "horizontal")
        if (pose.released && !this.played.released) {
            this.played.released = true
            this.feedback.clearLineEnd()
            for (let i = 0; i < WALL_SECTIONS; i++) {
                if (i >= this.played.tiles) { this.feedback.add(key(i), cell(i), 0, false); this.played.tiles++ }
                this.feedback.setState(key(i), "valid")
                this.feedback.setCounting(key(i), this.clock, 1)
            }
        }
        if (pose.built && !this.played.built) {
            this.played.built = true
            for (let i = 0; i < WALL_SECTIONS; i++) {
                this.feedback.remove(key(i))
                this.feedback.spark(cell(i), "built")
            }
            this.feedback.settle(this.art.filter(tile => tile.parent !== undefined).map(tile => tile.sprite))
        }
        this.feedback.update()
    }

    /** The light over the scene: the wall catching it as it goes up. */
    private drawFx = (t: number) => {
        const g = this.fx.clear()
        drawRipples(g, t, RIPPLES, at => buildingCursorPose(at, this.cursorStops()))
        if (t < BUILT_AT) return
        const ms = t - BUILT_AT
        const cx = WALL_X + WALL_W / 2, cy = WALL_Y + WALL_H * 0.6
        // One warm bloom as the row commits, then a band of light across the finished wall.
        const bloom = 1 - easeOutCubic(span(ms, 0, 700))
        if (bloom > 0) glowDisc(g, cx, cy, WALL_W * 0.6, REWARD_GOLD_HOT, 0.6 * bloom, 5)
        // Over the solid planks only: the picket tops have grass between them, which ADD turns lime.
        shineBand(g, WALL_X, WALL_Y + 12, WALL_W, WALL_H - 16, easeInOutCubic(span(ms, 60, 760)), 26, 0.5)
        sparkBurst(g, cx, cy, span(ms, 0, 650), 14, WALL_W * 0.3, WALL_W * 0.75, REWARD_GOLD, 0.4, 1.75)
    }

    private cursorStops = (): CursorStops => {
        const bar = this.readyBar
        return {
            rest: { x: SCENE_X + 7.2 * CELL, y: SCENE_Y + 3.4 * CELL },
            slot: { x: bar.x + TRAY_PAD + SLOT_PITCH + SLOT * 0.55, y: bar.y + TRAY_PAD + SLOT * 0.5 },
            start: { x: SCENE_X + (WALL_COL + 0.5) * CELL, y: SCENE_Y + (FACE_ROW + 0.5) * CELL },
            end: { x: SCENE_X + (WALL_COL + WALL_SECTIONS - 0.5) * CELL, y: SCENE_Y + (FACE_ROW + 0.5) * CELL },
        }
    }

    // First: the feedback destroys its own graphics, which the children sweep would otherwise
    // destroy a second time.
    protected teardown() {
        this.feedback.destroy()
    }
}
